import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Evoryn } from '../../Servcie/evoryn';
import { SearchService } from '../../Service/search';
import { ToastrNotificationService } from '../../Common/toastr-notification.service';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-product-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './product-list.html',
  styleUrls: ['./product-list.css']
})
export class ProductList implements OnInit, OnDestroy {

  // IMPORTANT: Use the same secret key in your main website!
  private readonly SECRET_KEY = 'Evoryn_Partner_Secret_2026';

  private readonly SINGLE_PRODUCT_BASE_URL = environment.SINGLE_PRODUCT_BASE_URL;
  private readonly COMBO_PRODUCT_BASE_URL = environment.COMBO_PRODUCT_BASE_URL;
  // private readonly BASE_URL = 'http://localhost:52307/product-details/';

  allProducts: any[] = [];
  filteredProducts: any[] = [];

  currentSearchText: string = '';
  private searchSubscription!: Subscription;

  constructor(
    private api: Evoryn,
    private searchService: SearchService,
    private toastr: ToastrNotificationService
  ) { }

  ngOnInit(): void {
    // Connect global search
    this.searchSubscription = this.searchService.currentSearch$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(text => {
        this.currentSearchText = text;
        this.applyFilter();
      });

    this.fetchProducts();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  fetchProducts() {
    this.api.fetchproduct().subscribe({
      next: (res: any) => {

        if (res.status && res.data) {
          const sessionUserId = sessionStorage.getItem('userID') || '0';

          this.allProducts = res.data.map((p: any, index: number) => {

            const isComboProduct = p.isCombo || p.isComboProduct || false;
            const affiliateLink = this.generateAffiliateLink(p.productSlug, sessionUserId, isComboProduct);

            return {
              srNo: (index + 1).toString().padStart(2, '0'),
              image: p.productImage, // Matches your JSON
              name: p.productName,   // Matches your JSON
              type: isComboProduct ? 'Combo Product' : 'Single Product',
              link: affiliateLink    // The secure link we just generated
            };
          });
        } else {
          this.allProducts = [];
        }
        this.applyFilter();
      },
      error: (err) => {
        this.toastr.showError('Failed to fetch products.');
        console.error(err);
      }
    });
  }

  // ==========================================
  // URL GENERATION LOGIC
  // ==========================================
  generateAffiliateLink(productSlug: string, userID: string, isCombo: boolean): string {
    try {
      // 1. User ID to Base64
      const base64UserID = btoa(userID);

      // 2. Encrypt the Base64 User ID
      const encryptedUserID = CryptoJS.AES.encrypt(base64UserID, this.SECRET_KEY).toString();

      // 3. URL Encode to make it safe for IIS servers
      const safeEncryptedUserID = encodeURIComponent(encryptedUserID);

      // 4. Construct Final URL: Product Slug in path, User ID in query
      const baseUrl = isCombo ? this.COMBO_PRODUCT_BASE_URL : this.SINGLE_PRODUCT_BASE_URL;
      return `${baseUrl}${productSlug}?ref=${safeEncryptedUserID}`;
    } catch (e) {
      console.error('Error generating link', e);
      return '';
    }
  }

  // ==========================================
  // COPY TO CLIPBOARD
  // ==========================================
  copyLink(link: string) {
    if (!link) return;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(link).then(() => {
        this.toastr.showSuccess('Affiliate link copied to clipboard!');
      }).catch(err => {
        this.toastr.showError('Failed to copy link.');
        console.error('Copy failed', err);
      });
    } else {
      // Fallback method using textarea and execCommand for environments where Clipboard API is blocked
      try {
        const textArea = document.createElement("textarea");
        textArea.value = link;
        
        // Prevent scrolling to bottom of page in MS Edge.
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          this.toastr.showSuccess('Affiliate link copied to clipboard!');
        } else {
          this.toastr.showError('Failed to copy link.');
        }
      } catch (err) {
        this.toastr.showError('Failed to copy link.');
        console.error('Fallback copy failed', err);
      }
    }
  }

  applyFilter() {
    if (!this.currentSearchText || this.currentSearchText.trim() === '') {
      this.filteredProducts = [...this.allProducts];
    } else {
      const searchStr = this.currentSearchText.toLowerCase().trim();
      this.filteredProducts = this.allProducts.filter(p =>
        (p.name && p.name.toLowerCase().includes(searchStr))
      );
    }
  }
}